import React, { useState, useEffect, useMemo } from 'react';
import { apiFetch } from '../../../shared/lib/apiClient';

const fmt = (v) => {
  if (v == null || v === 0) return '';
  return '$' + Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const CATEGORY_COLORS = {
  'Proteins': 'text-red-600',
  'Meat': 'text-red-600',
  'Poultry': 'text-orange-600',
  'Produce': 'text-emerald-600',
  'Dairy': 'text-yellow-600',
  'Dairy and Eggs': 'text-yellow-600',
  'Bakery': 'text-amber-600',
  'Beverage': 'text-sky-600',
  'Beverage Ingredients': 'text-sky-600',
  'Seafood': 'text-blue-600',
  'Frozen': 'text-indigo-600',
  'Dry Goods': 'text-stone-600',
  'Pantry and Ingredients': 'text-lime-600',
  'Condiments': 'text-violet-600',
  'Cleaning Supplies': 'text-cyan-600',
  'Operational Supplies': 'text-zinc-500',
  'Supplies': 'text-cyan-600',
  'Fees and Surcharges': 'text-rose-500',
  'Prepared Foods': 'text-pink-600',
  'Ingredients': 'text-lime-600',
  'Spices and Herbs': 'text-amber-600',
};

const getCatColor = (name) => CATEGORY_COLORS[name] || 'text-zinc-600';

const VendorTaxonomyView = ({ loaded }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(new Set());
  const [search, setSearch] = useState('');

  useEffect(() => {
    let cancelled = false;
    apiFetch('/api/v3/taxonomy/tree')
      .then(async (res) => {
        const payload = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(payload?.error || 'Failed to load');
        return payload;
      })
      .then((d) => {
        if (!cancelled) {
          setData(d.vendors || []);
          // Auto-expand all vendors
          const ids = new Set();
          (d.vendors || []).forEach(v => ids.add(`v:${v.vendor_id}`));
          setExpanded(ids);
        }
      })
      .catch((e) => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const stats = useMemo(() => {
    if (!data) return { vendors: 0, categories: 0, spend: 0 };
    let categories = 0;
    let spend = 0;
    for (const v of data) {
      categories += v.categories.length;
      spend += v.total_spend || 0;
    }
    return { vendors: data.length, categories, spend };
  }, [data]);

  const filtered = useMemo(() => {
    if (!data) return [];
    if (!search) return data;
    const q = search.toLowerCase();
    return data.filter(v => {
      if (v.vendor_name.toLowerCase().includes(q)) return true;
      if ((v.domain || '').toLowerCase().includes(q)) return true;
      return v.categories.some(c =>
        c.name.toLowerCase().includes(q) ||
        c.subcategories.some(s => s.name.toLowerCase().includes(q))
      );
    });
  }, [data, search]);

  const toggle = (key) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const expandAll = () => {
    const ids = new Set();
    (filtered || []).forEach(v => {
      ids.add(`v:${v.vendor_id}`);
      v.categories.forEach(c => ids.add(`v:${v.vendor_id}:c:${c.name}`));
    });
    setExpanded(ids);
  };

  const collapseAll = () => setExpanded(new Set());

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center gap-3 text-zinc-400">
          <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" className="opacity-25" /><path d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="3" strokeLinecap="round" className="opacity-75" /></svg>
          <span className="text-sm tracking-wide">Loading taxonomy...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-zinc-400 text-sm">{error}</p>
          <button onClick={() => window.location.reload()} className="mt-3 text-xs text-zinc-500 hover:text-zinc-700 underline">Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className={`transition-all duration-500 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-xl font-semibold text-zinc-900 tracking-tight">Vendor Taxonomy</h1>
        <p className="text-sm text-zinc-400 mt-1">
          {stats.vendors} vendor{stats.vendors !== 1 ? 's' : ''} — {stats.categories} categories
          {stats.spend > 0 && <> — {fmt(stats.spend)} total spend</>}
        </p>
      </div>

      {/* Controls */}
      <div className="flex items-center gap-3 mb-4">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search vendors, categories..."
          className="px-3 py-1.5 text-sm border border-zinc-200 rounded-md w-64 focus:outline-none focus:border-zinc-400"
        />
        <div className="ml-auto flex gap-2">
          <button onClick={expandAll} className="text-[10px] uppercase tracking-wider text-zinc-400 hover:text-zinc-600 transition-colors">Expand all</button>
          <span className="text-zinc-200">|</span>
          <button onClick={collapseAll} className="text-[10px] uppercase tracking-wider text-zinc-400 hover:text-zinc-600 transition-colors">Collapse</button>
        </div>
      </div>

      {/* Tree */}
      <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
        <div className="p-4 font-mono text-sm select-none">
          {filtered.length === 0 ? (
            <p className="text-sm text-zinc-400 text-center py-8 font-sans">
              {search ? 'No matches found' : 'No taxonomies yet — process invoices to generate vendor taxonomies'}
            </p>
          ) : (
            filtered.map((vendor, vi) => {
              const vKey = `v:${vendor.vendor_id}`;
              const vOpen = expanded.has(vKey);
              const isLastVendor = vi === filtered.length - 1;

              return (
                <div key={vendor.vendor_id}>
                  {/* Vendor row */}
                  <button
                    onClick={() => toggle(vKey)}
                    className="flex items-center w-full text-left hover:bg-zinc-50 rounded px-1 py-1 transition-colors group"
                  >
                    <span className="text-zinc-300 w-5 shrink-0">{vOpen ? '\u2513' : '\u2503'}</span>
                    <span className="text-zinc-300 mr-2">{vOpen ? '\u2500\u2500' : '\u2500\u2500'}</span>
                    <span className="font-semibold text-zinc-900 font-sans">{vendor.vendor_name}</span>
                    {vendor.domain && (
                      <span className="ml-2 text-[10px] bg-zinc-100 text-zinc-500 px-1.5 py-0.5 rounded font-sans">
                        {vendor.domain}
                      </span>
                    )}
                    <span className="ml-auto text-xs text-zinc-400 font-sans tabular-nums">
                      {vendor.total_items > 0 && <span className="mr-3">{vendor.total_items} items</span>}
                      {vendor.total_spend > 0 && <span className="font-medium text-zinc-600">{fmt(vendor.total_spend)}</span>}
                    </span>
                    <span className="text-[10px] text-zinc-300 ml-3 font-sans">v{vendor.version}</span>
                  </button>

                  {/* Categories */}
                  {vOpen && vendor.categories.map((cat, ci) => {
                    const cKey = `v:${vendor.vendor_id}:c:${cat.name}`;
                    const cOpen = expanded.has(cKey);
                    const isLastCat = ci === vendor.categories.length - 1;
                    const catColor = getCatColor(cat.name);
                    const connector = isLastVendor && isLastCat ? '\u2514' : '\u2502';
                    const branch = isLastCat ? '\u2514' : '\u251C';

                    return (
                      <div key={cat.name}>
                        {/* Category row */}
                        <button
                          onClick={() => toggle(cKey)}
                          className="flex items-center w-full text-left hover:bg-zinc-50 rounded px-1 py-0.5 transition-colors"
                          style={{ paddingLeft: 20 }}
                        >
                          <span className="text-zinc-200 w-5 shrink-0">{branch}</span>
                          <span className="text-zinc-200 mr-2">{'\u2500\u2500'}</span>
                          <span className={`font-medium font-sans ${catColor}`}>{cat.name}</span>
                          {cat.unlisted && (
                            <span className="ml-1.5 text-[9px] bg-amber-50 text-amber-600 px-1 rounded font-sans">unlisted</span>
                          )}
                          <span className="ml-auto text-xs text-zinc-400 font-sans tabular-nums">
                            {cat.item_count > 0 && <span className="mr-3">{cat.item_count}</span>}
                            {cat.spend > 0 && <span className="text-zinc-500">{fmt(cat.spend)}</span>}
                          </span>
                        </button>

                        {/* Subcategories */}
                        {cOpen && cat.subcategories.map((sub, si) => {
                          const isLastSub = si === cat.subcategories.length - 1;
                          const subBranch = isLastSub ? '\u2514' : '\u251C';

                          return (
                            <div
                              key={sub.name}
                              className="flex items-center px-1 py-0.5 hover:bg-zinc-50/50 rounded transition-colors"
                              style={{ paddingLeft: 44 }}
                            >
                              <span className="text-zinc-200 w-5 shrink-0">{subBranch}</span>
                              <span className="text-zinc-200 mr-2">{'\u2500\u2500'}</span>
                              <span className="text-zinc-600 font-sans text-sm">{sub.name}</span>
                              {sub.unlisted && (
                                <span className="ml-1.5 text-[9px] bg-amber-50 text-amber-600 px-1 rounded font-sans">unlisted</span>
                              )}
                              <span className="ml-auto text-xs font-sans tabular-nums">
                                {sub.item_count > 0 && <span className="text-zinc-400 mr-3">{sub.item_count}</span>}
                                {sub.spend > 0 && <span className="text-zinc-500">{fmt(sub.spend)}</span>}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default VendorTaxonomyView;
